import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import MdZoomOut from 'src/shared/material_icons/action/zoom_out.svg';
import MdZoomIn from 'src/shared/material_icons/action/zoom_in.svg';
import styled from 'styled-components';
import { Area } from './areaDesigner.model';
import { AreaDesignerEvents, AreaDesignerManager } from './areaDesignerManager';
import { Tool, Unit } from './helpers';
import { Icon } from '../icon';

const Canvas = styled.canvas`
    display: block;
    width: 100%;
    height: 100%;
`;

const AreaDesignerViewerContainer = styled.div`
    min-height: 0;
    min-width: 0;
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
`;

const AreaDesignerViewerZoomIcons = styled.div`
    position: absolute;
    left: 12px;
    top: calc(50% - 36px);
    display: flex;
    flex-direction: column;
    gap: 4px;

    ${Icon} {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 6px;
        color: #263238;
        background: white;
        border: 1px solid #e5e7eb;
        cursor: pointer;
        transition: all 0.12s ease;

        &:hover {
            background: #f3f4f6;
        }

        svg {
            width: 18px;
            height: 18px;
        }
    }
`;

interface AreaDesignerViewerProps {
    unit?: Unit;
    selectedItemGuid?: string | null;
    areas: Area[];
    sideImageUrl: string;
    ppcm?: number;
    isViewer?: boolean;
    tool: Tool;
    isResizable?: boolean;
    resizableWidth?: number;
    resizableHeight?: number;
    resizableColumns?: number;
    resizableRows?: number;
}

export const AreaDesignerViewer = React.forwardRef<{ resize: () => void }, AreaDesignerViewerProps & AreaDesignerEvents>((props, ref) => {
    const parentRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [manager, setManager] = useState<AreaDesignerManager | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [, setIsReadyCanvas] = useState(false);

    // Init
    useEffect(() => {
        if (canvasRef.current) setManager(AreaDesignerManager.create(canvasRef.current, props.isViewer ? 'VIEWER' : 'EDITOR'));

        return () => manager?.destroy();
    }, [canvasRef.current]);

    // Side image
    useEffect(() => {
        manager?.setSideImage(props.sideImageUrl);
    }, [manager, props.sideImageUrl, canvasRef.current]);

    useEffect(() => {
        setIsReady(true);
    }, []);

    useEffect(() => {
        if (isReady) setIsReadyCanvas(true);
    }, [isReady]);

    if (props.unit) manager?.setUnit(props.unit);

    if (props.ppcm !== undefined) manager?.setPPCM(props.ppcm);

    // Keep data synced
    manager?.setAreas(props.areas);
    manager?.setEvents(props);
    manager?.setTool(props.tool);
    manager?.setSelectedArea(props.selectedItemGuid);
    manager?.updateResizable(
        props.isResizable ?? false,
        props.resizableWidth ?? 1,
        props.resizableHeight ?? 1,
        props.resizableColumns ?? 1,
        props.resizableRows ?? 1
    );
    manager?.update();

    const resize = () => {
        manager?.updateCanvasSize();
    };

    const hideRuler = () => {
        manager?.hideRuler();
    };

    const fitToBounds = () => {
        manager?.fitToBounds();
    };

    useImperativeHandle(ref, () => ({
        resize,
        hideRuler,
        fitToBounds
    }));

    return (
        <AreaDesignerViewerContainer ref={parentRef}>
            {parentRef.current && <Canvas ref={canvasRef} width={parentRef.current.offsetWidth} height={parentRef.current.offsetHeight} />}

            {!props.isViewer && (
                <AreaDesignerViewerZoomIcons>
                    <Icon
                        $hoverable
                        onClick={() => {
                            manager?.zoomIn();
                        }}
                    >
                        <MdZoomIn />
                    </Icon>
                    <Icon
                        $hoverable
                        onClick={() => {
                            manager?.zoomOut();
                        }}
                    >
                        <MdZoomOut />
                    </Icon>
                </AreaDesignerViewerZoomIcons>
            )}
        </AreaDesignerViewerContainer>
    );
});
